import { Box, Container, Link, styled, Typography } from "@mui/material";
import React from "react";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
}));

const Figure = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    backgroundColor: "#fafafa",
}));

const Screenshot = styled("img")({
    display: "block",
    maxWidth: "100%",
    height: "auto",
    borderRadius: 6,
    border: "1px solid #d0d0d0",
});

/**
 * Public, login-free page that documents FundsyFi's SMS opt-in / consent
 * workflow for A2P 10DLC (TCR) campaign review. The campaign's message_flow
 * field links here so reviewers can publicly verify how end users consent.
 */
const SmsOptInEvidence = () => {
    return (
        <React.Fragment>
            <PageContainer maxWidth="md">
                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}
                >
                    SMS Opt-In &amp; Consent Evidence
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                    Program: FundsyFi Payment Reminders &nbsp;|&nbsp; Last updated: June 9,
                    2026
                </Typography>

                <Section>
                    <Typography variant="body1" paragraph>
                        This page is provided so that carriers and The Campaign Registry
                        (TCR) can publicly verify how end users consent to receive text
                        messages through FundsyFi. FundsyFi is an invoicing and
                        accounts-receivable platform used by small businesses. Through
                        FundsyFi, those businesses send <b>transactional</b> payment
                        reminders, due/past-due invoice notices, and payment confirmations to
                        their own invoiced customers. FundsyFi never sends marketing or
                        promotional messages, and never messages anyone outside an
                        established business/billing relationship.
                    </Typography>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Who receives messages
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Recipients are <b>existing customers of a FundsyFi business</b> who
                        have an open or past-due invoice and who provided their mobile number
                        directly to that business. Consent is established through the
                        business&rsquo;s relationship with its customer, not collected by
                        FundsyFi from the public. FundsyFi acts on the business&rsquo;s
                        behalf to deliver the transactional notifications the customer agreed
                        to receive.
                    </Typography>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        How consent is collected (opt-in paths)
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        1. Business&ndash;customer billing relationship
                    </Typography>
                    <Typography variant="body1" paragraph>
                        When a customer purchases services, is invoiced, or sets up a billing
                        account with a FundsyFi business, they provide their mobile number to
                        that business and agree to receive transactional notifications about
                        their invoices and payments. Before any SMS is enabled, the business
                        must confirm in FundsyFi that it has the customer&rsquo;s consent
                        before text reminders can be enabled for that customer.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        2. Website phone field (FundsyFi account holders)
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Account holders can also add a mobile number on the FundsyFi sign-up
                        form at{" "}
                        <Link href="https://www.fundsyfi.com/circle/sign-up">
                            https://www.fundsyfi.com/circle/sign-up
                        </Link>
                        , where the phone field is labeled &ldquo;Optional &ndash; add your
                        phone number to receive SMS payment reminders.&rdquo; A dedicated SMS
                        consent box, separate from the Terms &amp; Conditions box, is{" "}
                        <b>unchecked by default</b> and must be actively checked by the user.
                        Its label reads: &ldquo;I agree to receive transactional SMS payment
                        reminders and account notifications from FundsyFi at the number
                        provided. Message frequency varies; message &amp; data rates may
                        apply. Reply STOP to opt out, HELP for help,&rdquo; with links to the{" "}
                        <Link href="https://www.fundsyfi.com/privacy-policy">
                            Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link href="https://www.fundsyfi.com/terms-of-service">
                            Terms of Service
                        </Link>
                        . The SMS opt-in is optional and is not required to create an
                        account.
                    </Typography>
                    <Figure>
                        <Screenshot
                            src="/images/sms-evidence/signup-phone-optin.png"
                            alt="FundsyFi sign-up form showing the labeled optional phone number field and a dedicated, unchecked SMS consent checkbox stating the user agrees to receive transactional SMS payment reminders and account notifications, with message frequency and rate disclosures, STOP/HELP instructions, and links to the Privacy Policy and Terms of Service."
                        />
                        <Typography variant="caption" color="text.secondary">
                            Figure 1 &mdash; FundsyFi sign-up form: labeled optional phone
                            field and a dedicated, unchecked SMS consent checkbox with full
                            disclosures and STOP/HELP instructions.
                        </Typography>
                    </Figure>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Required disclosures
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>
                            <Typography variant="body1">
                                <b>Message frequency varies</b> based on the recipient&rsquo;s
                                outstanding invoices and account activity.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                <b>Message and data rates may apply.</b>
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                Mobile information is <b>never shared with third parties</b> or
                                affiliates for marketing, and opt-in data is never sold &mdash;
                                see{" "}
                                <Link href="https://www.fundsyfi.com/privacy-policy">
                                    Privacy Policy, Section 13
                                </Link>
                                .
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                Full SMS program terms, including HELP and STOP instructions, are
                                published in the{" "}
                                <Link href="https://www.fundsyfi.com/terms-of-service">
                                    Terms of Service, Section 16 (SMS Program)
                                </Link>
                                .
                            </Typography>
                        </li>
                    </Box>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Opt-out and help (STOP / START / HELP)
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Recipients can opt out at any time by replying <b>STOP</b> to any
                        message. FundsyFi processes opt-outs automatically and immediately
                        stops further messages to that number; the recipient may receive one
                        final confirmation. To opt back in, reply <b>START</b>. For help,
                        reply <b>HELP</b> or email{" "}
                        <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
                    </Typography>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Sample messages
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>
                            <Typography variant="body1">
                                FundsyFi: Hi Jordan, your invoice #1042 for $450 is past due.
                                Pay: https://www.fundsyfi.com/pay/a1b2c3 Reply STOP to opt out,
                                HELP for help.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                FundsyFi: Reminder&mdash;invoice #1042 is due tomorrow. Pay:
                                https://www.fundsyfi.com/pay/a1b2c3 Reply STOP to opt out, HELP
                                for help.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                FundsyFi: Payment of $450 for invoice #1042 received. Thank you!
                                Reply STOP to opt out.
                            </Typography>
                        </li>
                    </Box>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Contact
                    </Typography>
                    <Typography variant="body1">
                        <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>
                    </Typography>
                    <Typography variant="body1">Houston, Texas 77085</Typography>
                </Section>
            </PageContainer>
            <Footer />
        </React.Fragment>
    );
};

export default SmsOptInEvidence;
